import React from "react";
import { responseHero } from "../../utils/axiosInterceptor";
import { getNewsLetterViewers } from "../../api/getNewsLetterViewers";
import { getNewsLetterVideos, PickedNewsLetterViewerVideos } from "../../api/getNewsLetterVideos";
import { NewsletterView } from "../../models/newsletterview.model";
import { getNewsLetterNonViewers } from "../../api/getNewsLetterNonViewers";

export default function NewsLetterViewers() {
    const [videoIds, setVideoIds] = React.useState<PickedNewsLetterViewerVideos[]>([]);
    const [selectedVideoId, setSelectedVideoId] = React.useState<string>("");
    const [viewers, setViewers] = React.useState<NewsletterView[]>([]);
    
    const [nonViewersList, setNonViewersList] = React.useState<{name: string, email: string}[]>([]);
    
    const [loading, setLoading] = React.useState<boolean>(false);
    const [isModalOpen, setIsModalOpen] = React.useState<boolean>(false);

    const currentNewsLetterId = videoIds.find(v => v.videoId === selectedVideoId)?.newsLetterId;

    React.useEffect(() => {
        const fetchVideos = async () => {
            const res = await getNewsLetterVideos();
            setVideoIds(res);
            if (res.length > 0) setSelectedVideoId(res[0].videoId);
        };
        fetchVideos();
    }, []);

    React.useEffect(() => {
        if (!selectedVideoId) return;
        
        const fetchData = async () => {
            setLoading(true);
            try {
                const viewerRes = await getNewsLetterViewers(selectedVideoId);
                setViewers(viewerRes);
        
                if (currentNewsLetterId) {
                    // Itt a rawData pontosan az az objektum, amit bemásoltál
                    const rawData = await getNewsLetterNonViewers(currentNewsLetterId, selectedVideoId) as any;
                    
                    // Ellenőrizzük a láncot: rawData -> newsletterGroup -> NewsLetterSubscription
                    const subscriptions = rawData?.newsletterGroup?.NewsLetterSubscription || [];
        
                    const flattenedUsers = subscriptions.map((sub: any) => ({
                        name: sub.user?.name || "Nincs név",
                        email: sub.user?.email || "Nincs e-mail"
                    }));
                    
                    setNonViewersList(flattenedUsers);
                } else {
                    setNonViewersList([]);
                }
            } catch (error) {
                console.error("Hiba az adatok lekérésekor:", error);
                setNonViewersList([]);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [selectedVideoId, currentNewsLetterId]);

    return (
        <div className="p-8 min-h-screen bg-[#f9fafb] text-slate-700 font-sans selection:bg-indigo-100">
            <div className="max-w-6xl mx-auto">
                
                {/* Header Section */}
                <div className="mb-8 flex flex-col md:flex-row md:items-end justify-between gap-6">
                    <div>
                        <div className="flex items-center gap-3">
                            <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Videó megtekintések</h1>
                            {/* NEW: Counter Badge next to title */}
                            {!loading && (
                                <span className="px-2.5 py-0.5 bg-indigo-50 text-indigo-600 text-xs font-bold rounded-full border border-indigo-100">
                                    {viewers.length} összesen
                                </span>
                            )}
                        </div>
                        <p className="text-sm text-slate-500 mt-1 flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full bg-indigo-500 animate-pulse"></span>
                            Human D.C. hírlevél statisztika
                        </p>
                    </div>

                    <div className="flex items-center gap-3">
                        {currentNewsLetterId && (
                            <button 
                                onClick={() => setIsModalOpen(true)}
                                className="group px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-xs font-semibold hover:border-indigo-300 hover:text-indigo-600 transition-all duration-200 shadow-sm flex items-center gap-2"
                            >
                                <span className="relative flex h-2 w-2">
                                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-orange-400 opacity-75"></span>
                                    <span className="relative inline-flex rounded-full h-2 w-2 bg-orange-500"></span>
                                </span>
                                Hiányzó ({nonViewersList.length})
                            </button>
                        )}

                        <div className="relative group">
                            <select 
                                value={selectedVideoId}
                                onChange={(e) => setSelectedVideoId(e.target.value)}
                                className="appearance-none w-72 pl-4 pr-10 py-2.5 bg-white border border-slate-200 rounded-xl text-sm font-medium focus:ring-4 focus:ring-indigo-500/5 focus:border-indigo-500 outline-none transition-all cursor-pointer shadow-sm"
                            >
                                {videoIds.map((v) => (
                                    <option key={v.videoId} value={v.videoId}>
                                        {v.subject} — {v.videoName}
                                    </option>
                                ))}
                            </select>
                            <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-slate-400">
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path></svg>
                            </div>
                        </div>
                    </div>
                </div>

                {/* NEW: Summary Info Bar */}
                <div className="mb-4 flex items-center justify-between px-2">
                    <div className="text-[11px] font-bold text-slate-400 uppercase tracking-widest">
                        Aktuális lista
                    </div>
                    <div className="text-xs text-slate-500">
                        A videót a hírlevélben eddig <span className="font-semibold text-slate-900">{viewers.length} ügyfél nyitotta meg.</span>
                    </div>
                </div>

                {/* Table Card */}
                <div className="bg-white rounded-2xl border border-slate-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full text-left border-collapse">
                            <thead>
                                <tr className="bg-slate-50/50">
                                    <th className="px-6 py-4 text-[11px] font-bold text-slate-400 uppercase tracking-widest">Név / E-mail</th>
                                    <th className="px-6 py-4 text-[11px] font-bold text-slate-400 uppercase tracking-widest">Állapot</th>
                                    <th className="px-6 py-4 text-[11px] font-bold text-slate-400 uppercase tracking-widest text-right">Időpont</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-50">
                                {loading ? (
                                    <tr><td colSpan={3} className="py-20 text-center text-slate-400 italic font-light">Adatok szinkronizálása...</td></tr>
                                ) : viewers.length > 0 ? (
                                    viewers.map((viewer) => (
                                        <tr key={viewer.id} className="group hover:bg-slate-50/80 transition-all duration-150">
                                            <td className="px-6 py-5">
                                                <div className="font-semibold text-slate-900 group-hover:text-indigo-600 transition-colors">{viewer.user?.name || "Névtelen Felhasználó"}</div>
                                                <div className="text-xs text-slate-400 mt-0.5">{viewer.email}</div>
                                            </td>
                                            <td className="px-6 py-5">
                                                <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider border ${
                                                    viewer.status === "COMPLETED" 
                                                    ? "border-emerald-100 text-emerald-700 bg-emerald-50/50" 
                                                    : "border-amber-100 text-amber-700 bg-amber-50/50"
                                                }`}>
                                                    <span className={`w-1 h-1 rounded-full ${viewer.status === "COMPLETED" ? "bg-emerald-500" : "bg-amber-500"}`}></span>
                                                    {viewer.status}
                                                </span>
                                            </td>
                                            <td className="px-6 py-5 text-right text-sm text-slate-500 font-medium">
                                                {new Date(viewer.createdAt).toLocaleString('hu-HU', { dateStyle: 'medium', timeStyle: 'short' })}
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr><td colSpan={3} className="py-20 text-center text-slate-400 font-light">Még nincsenek megtekintések a kiválasztott videóhoz.</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

    {/* Enhanced Modal */}
    {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-6">
            <div className="absolute inset-0 bg-slate-900/20 backdrop-blur-md" onClick={() => setIsModalOpen(false)}></div>
            
            <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-in fade-in slide-in-from-bottom-4 duration-300">
                <div className="px-6 py-5 border-b border-slate-50 flex justify-between items-center bg-white">
                    <div>
                        <h2 className="font-bold text-slate-900">Hiányzó megtekintések</h2>
                        <p className="text-xs text-slate-400 mt-0.5">{nonViewersList.length} címzett nem indította el a videót</p>
                    </div>
                    <button onClick={() => setIsModalOpen(false)} className="p-2 hover:bg-slate-50 rounded-lg transition-colors text-slate-400">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path></svg>
                    </button>
                </div>
                
                <div className="max-h-[50vh] overflow-y-auto px-2 py-2">
                    {nonViewersList.length > 0 ? (
                        nonViewersList.map((user, idx) => (
                            <div key={idx} className="p-4 rounded-xl hover:bg-slate-50 transition-colors flex items-center gap-3">
                                <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-[10px] font-bold text-slate-500 uppercase">
                                    {user.name?.substring(0,2) || "??"}
                                </div>
                                <div>
                                    <div className="font-semibold text-slate-800 text-sm">{user.name}</div>
                                    <div className="text-[11px] text-slate-400">{user.email}</div>
                                </div>
                            </div>
                        ))
                    ) : (
                        <div className="py-12 text-center">
                            <div className="text-3xl mb-2">🎉</div>
                            <div className="text-sm text-slate-500 font-medium">Mindenki megtekintette!</div>
                        </div>
                    )}
                </div>
                
                <div className="p-5 bg-slate-50/50 border-t border-slate-50">
                    <button 
                        onClick={() => setIsModalOpen(false)}
                        className="w-full py-3 bg-slate-900 text-white rounded-xl text-sm font-semibold hover:bg-slate-800 transition-all active:scale-[0.98] shadow-lg shadow-slate-200"
                    >
                        Rendben
                    </button>
                </div>
            </div>
        </div>
    )}
</div>
    );
}